import { useEffect, useState } from 'react';
import api from '@/lib/api';
import KpiCard from '@/components/dashboard/KpiCard';
import ChartCard from '@/components/dashboard/ChartCard';
import { filtersToParams } from '@/components/dashboard/DashboardFilters';
import { Bot, MessageCircle, Sparkles, Clock, Zap } from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';

const PIE_COLORS = ['#A5D62C', '#1B9CEA', '#F59E0B', '#A855F7', '#EC4899'];

function errStatus(e) {
  return e?.response?.status || 0;
}

/**
 * Dashboard de IA — mismos filtros del header (AND tipo Excel).
 * Seguimiento = recetas v2 (/comm-logic-recipes/monitoreo). El viejo
 * /nurturing/stats no existe: no se llama.
 */
export default function IATab({ filters, vertical = 'generic' }) {
  void vertical;
  const [stats, setStats] = useState(null);
  const [follow, setFollow] = useState(null);
  const [aiNote, setAiNote] = useState('');
  const [followNote, setFollowNote] = useState('');
  const [loading, setLoading] = useState(true);
  const paramsKey = JSON.stringify(filtersToParams(filters || {}));

  useEffect(() => {
    const params = filtersToParams(filters || {});
    if (params.seller_id) params.assigned_to = params.seller_id;
    setLoading(true);
    setAiNote('');
    setFollowNote('');
    Promise.all([
      api.get('/ai-agent/stats', { params }).catch((e) => {
        if (errStatus(e) === 403) setAiNote('El módulo de IA no está en el plan de esta cuenta.');
        else setAiNote('No se pudieron cargar las conversaciones de IA.');
        return { data: null };
      }),
      api.get('/comm-logic-recipes/monitoreo').catch((e) => {
        if (errStatus(e) === 403) setFollowNote('Las reglas de seguimiento no están en el plan. Configúralas en Recetas de seguimiento cuando el plan las incluya.');
        else setFollowNote('No hay datos de seguimiento todavía.');
        return { data: null };
      }),
    ]).then(([ai, rec]) => {
      setStats(ai.data);
      const m = rec.data?.metricas || null;
      if (m) {
        const sent = Number(m.entregados_hoy || 0);
        const fail = Number(m.fallaron_hoy || 0);
        const denom = sent + fail;
        setFollow({
          active_sequences: Number(m.en_cola || 0),
          messages_sent: sent,
          failed: fail,
          replied: Number(m.respondieron || 0),
          stuck: Number(m.atascados || 0),
          success_rate: denom ? Math.round((sent / denom) * 100) : undefined,
        });
      } else {
        setFollow(null);
      }
      setLoading(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey]);

  const totalConvs = stats?.total_conversations ?? 0;
  const aiActive = stats?.active ?? 0;
  const conversion = stats?.conversion_rate ?? 0;
  const avgMsgs = stats?.avg_messages_per_conv ?? 0;
  const followActive = follow?.active_sequences ?? 0;
  const followSent = follow?.messages_sent ?? 0;

  const channelData = [
    { name: 'IA', value: stats?.ai_messages || 0 },
    { name: 'Lead', value: stats?.user_messages || 0 },
    { name: 'Humano', value: stats?.human_messages || 0 },
  ].filter(d => d.value > 0);

  const followEmpty = !loading && !follow;
  const followEmptyMsg = followNote || 'Sin reglas de seguimiento activas en este periodo.';

  return (
    <div className="space-y-6" data-testid="tab-ia-content">
      {aiNote && (
        <p className="text-xs text-amber-300/90 bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2" data-testid="ia-note">{aiNote}</p>
      )}
      <div className="flex overflow-x-auto gap-3 pb-1 sm:grid sm:grid-cols-4">
        <KpiCard icon={MessageCircle} label="Conversaciones IA" value={loading ? '—' : totalConvs} sub={`${aiActive} activas`} color="cyan" loading={loading} testid="ia-kpi-convs" />
        <KpiCard icon={Bot} label="Conversión" value={loading ? '—' : `${conversion}%`} sub="tomadas + cerradas" color="amber" loading={loading} testid="ia-kpi-handoff" />
        <KpiCard icon={Clock} label="Msgs / conv" value={loading ? '—' : avgMsgs} sub="promedio" color="emerald" loading={loading} testid="ia-kpi-response" />
        <KpiCard icon={Sparkles} label="Seguimiento en cola" value={loading ? '—' : (followEmpty ? '—' : followActive)} sub={followEmpty ? 'sin recetas' : `${followSent} entregados hoy`} color="violet" loading={loading} testid="ia-kpi-nurturing" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard title="Mensajes por rol" icon={MessageCircle} loading={loading} empty={!channelData.length} emptyMessage={aiNote || 'Sin mensajes en este filtro'} testid="ia-chart-channels" tabKey="ia" chartId="ia-chart-channels">
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie data={channelData} dataKey="value" nameKey="name" innerRadius={50} outerRadius={90} paddingAngle={2} label={{ fill: '#cbd5e1', fontSize: 10 }}>
                {channelData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
              </Pie>
              <Tooltip contentStyle={{ background: '#1F2833', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, fontSize: 11 }} />
              <Legend wrapperStyle={{ fontSize: 10 }} />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Reglas de seguimiento" icon={Zap} loading={loading} empty={followEmpty} emptyMessage={followEmptyMsg} testid="ia-chart-nurturing" tabKey="ia" chartId="ia-chart-nurturing">
          <div className="space-y-3 px-2 py-4">
            <div className="flex items-center justify-between p-3 rounded-lg bg-black/20 border border-violet-500/20">
              <div>
                <p className="text-xs text-muted-foreground">En cola ahora</p>
                <p className="text-2xl font-bold text-violet-400 font-heading">{followActive}</p>
              </div>
              <Zap className="w-6 h-6 text-violet-400/40" />
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg bg-black/20 border border-cyan-500/20">
              <div>
                <p className="text-xs text-muted-foreground">Entregados hoy</p>
                <p className="text-2xl font-bold text-cyan-400 font-heading">{followSent}</p>
              </div>
              <MessageCircle className="w-6 h-6 text-cyan-400/40" />
            </div>
            {follow?.success_rate !== undefined && (
              <div className="flex items-center justify-between p-3 rounded-lg bg-black/20 border border-emerald-500/20">
                <div>
                  <p className="text-xs text-muted-foreground">Tasa de entrega hoy</p>
                  <p className="text-2xl font-bold text-emerald-400 font-heading">{follow.success_rate}%</p>
                </div>
                <Sparkles className="w-6 h-6 text-emerald-400/40" />
              </div>
            )}
            {follow?.replied > 0 && (
              <p className="text-[11px] text-muted-foreground">Respondieron y se canceló la secuencia: {follow.replied}</p>
            )}
          </div>
        </ChartCard>
      </div>
    </div>
  );
}
